import React, { useEffect, useRef, useState } from "react";
import { AlertBox, AlertBoxType } from "./Common/AlertBox";
import { PatientDetails, Session } from "../models";
import { BuildUrl, FormatName, METHOD, sendGAEvent } from "../helpers";

interface Props {
  session: Session;
  patient: PatientDetails;
  feeText: string;
  setIsError: Function;
  paymentResult: Function;
}

const Payment = React.memo(
  ({ session, patient, feeText, setIsError, paymentResult }: Props) => {
    const [isLoading, setisLoading] = useState(true);
    const paymentEventRecieved = useRef(false);

    // Payment window will send a postMessage event with payment result
    window.addEventListener(
      "message",
      (event) => {
        const data = event.data;
        if (!data || data.result == null) {
          return;
        }
        if (!paymentEventRecieved.current) {
          const isSuccess = data.result === "1";
          sendGAEvent({
            event: isSuccess ? "purchase" : "payment_failure",
            step: 13,
            step_name: "Purchase",
            user_id: patient.maskedEmail,
            extra: {
              consult_type: session.consult?.name,
              ecommerce: {
                value: session.consult?.price,
                currency: "AUD",
                items: [
                  {
                    item_name: session.consult?.name
                  }
                ]
              }
            }
          });
          paymentResult(isSuccess);
          paymentEventRecieved.current = true;
        }

        // 25 minute timeout
        let timeoutId;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {}, 1500000);
      },
      false
    );

    useEffect(() => {
      async function loadPaymentForm() {
        try {
          const formData = new FormData();
          formData.append("cid", session.cid.toString());
          formData.append(
            "apptype",
            session.consult?.itemNumber.toString() || ""
          );
          formData.append(
            "paymentAmount",
            (session.consult?.price || 0).toString()
          );
          formData.append("pname", FormatName(patient));
          formData.append("pid", patient.prn);
          const today = new Date();
          const dd = String(today.getDate()).padStart(2, "0");
          const mm = String(today.getMonth() + 1).padStart(2, "0"); //January is 0!
          const yyyy = today.getFullYear();

          formData.append("date", `${yyyy}-${mm}-${dd}`);
          formData.append("timeslot", "00:00");
          formData.append("duration", "10");
          formData.append("fmem", "");
          formData.append("docid", "0");
          formData.append("bookreason", "");
          const url = BuildUrl("load_payment");
          const response = await fetch(url, {
            method: METHOD.POST,
            headers: {
              "X-Requested-With": "XMLHttpRequest"
            },
            body: formData
          });

          if (response.body) {
            let html = "";
            const reader = response.body.getReader();
            while (true) {
              // The `read()` method returns a promise that
              // resolves when a value has been received.
              const { done, value } = await reader.read();
              // Result objects contain two properties:
              // `done`  - `true` if the stream has already given you all its data.
              // `value` - Some data. Always `undefined` when `done` is `true`.
              if (done) {
                let paymentUrl = "";
                let startIndex = html.indexOf(
                  process.env.REACT_APP_PAYMENT_URL || ""
                );
                if (startIndex > 0) {
                  paymentUrl = html.substring(startIndex);
                  paymentUrl = paymentUrl.slice(0, paymentUrl.indexOf(`'`));

                  const payFormElement: HTMLIFrameElement =
                    document.getElementById("payFrm") as HTMLIFrameElement;
                  if (payFormElement === null) {
                    throw new Error(`Pay Form is not mounted`);
                  } else {
                    payFormElement.src = paymentUrl;
                    setisLoading(false);
                  }
                } else {
                  setIsError(true);
                  console.log("Counld not find payment url");
                }
                return html;
              }
              html += new TextDecoder().decode(value);
            }
          }
        } catch (e) {
          console.log(e);
        }
      }
      loadPaymentForm();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div
        className="cs-chkin-form-panel js-active"
        data-animation="fadeIn"
        id="paymentPanel"
      >
        <div className="col-11 d-flex flex-column rowGap-2">
          <p className="cs-chkin-form-step-heading mb-4">
            Enter payment details
          </p>

          <AlertBox type={AlertBoxType.Info}>{feeText}</AlertBox>

          <div id="payment_form">
            {isLoading && (
              <div id="loadPform" className="row">
                <div className="col-md-8 col-md-offset-2">
                  <p
                    className="alert alert-info text-center"
                    style={{ fontSize: 16, color: "#14B8E7" }}
                  >
                    Processing your request. Please wait...
                  </p>
                </div>
              </div>
            )}
            {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
            <iframe
              id="payFrm"
              name="payFrm"
              width="100%"
              height="400"
              style={{ border: 0 }}
              data-pbk="MA=="
              data-pm="MDAwMDAwMDAwMA=="
            ></iframe>
          </div>
        </div>
      </div>
    );
  }
);

export default Payment;
